import { useDebounce } from '@uidotdev/usehooks';
import clsx from 'clsx';
import { Plus } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom';

import { useMatter } from '@/hooks/useMatter';
import { useMostInViewport } from '@/hooks/useMostInViewport';
import { useGetIncidentsQuery } from '@/services/api/incidentService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';
import {
  MedicalProvider,
  ProviderStatus,
} from '@/services/types/treatment-types';

import { AddProviderModal } from '../provider-directory/AddProviderModal';
import { Button } from '../ui/catalyst/button';
import { Card } from '../ui/v3/Card';
import { TreatmentOverviewNew } from './TreatmentOverviewNew';
import { ProviderOverview } from './v3/provider/ProviderDetailsPage';
import { RequestPageModal } from './v3/request/modals/RequestPageModal';
import { TreatmentSummary } from './v3/treatment/TreatmentSummaryPage';

type ActiveSection = { kind: 'summary' } | { kind: 'provider'; index: number };

interface TreatmentSidebarProps {
  summaryRef: React.RefObject<HTMLDivElement>;
  providerRefs: React.RefObject<HTMLDivElement[]>;
  providers: MedicalProvider[];
  containerRef: React.RefObject<HTMLDivElement>;
  onActiveSectionChange?: (section: ActiveSection) => void;
  onAddProvider: () => void;
}

interface TreatmentOutletContext {
  useNewDesign: boolean;
}

const TreatmentSidebar = ({
  summaryRef,
  providerRefs,
  providers,
  containerRef,
  onActiveSectionChange,
  onAddProvider,
}: TreatmentSidebarProps) => {
  // Force re-render after refs are populated (they're set during commit phase)
  const [, forceUpdate] = useState(0);
  useLayoutEffect(() => {
    forceUpdate((n) => n + 1);
  }, [providers.length]);

  const { activeIndex, scrollToIndex } = useMostInViewport(
    containerRef as React.RefObject<HTMLElement>,
    [summaryRef.current, ...(providerRefs.current ?? [])],
  );
  const debouncedActiveIndex = useDebounce(activeIndex, 50);

  const activeSection: ActiveSection = useMemo(() => {
    if (debouncedActiveIndex === -1 || debouncedActiveIndex === 0) {
      return { kind: 'summary' };
    }
    return { kind: 'provider', index: debouncedActiveIndex - 1 };
  }, [debouncedActiveIndex]);

  const isActive = (index: number) =>
    activeSection.kind === 'provider' && activeSection.index === index;

  const baseButtonStyle = 'flex justify-start py-1! px-2! rounded-sm! text-xs';

  const buttonStyles = {
    active: `${baseButtonStyle} bg-[#F1EFFF] text-[#211180]! hover:bg-[#e2deff]!`,
    inactive: `${baseButtonStyle} text-gray-500!`,
  };

  useEffect(() => {
    onActiveSectionChange?.(activeSection);
  }, [activeSection, onActiveSectionChange]);

  return (
    <Card className="flex flex-col gap-0.5 h-fit w-[280px] p-3! sticky top-0">
      <Button
        onClick={() => {
          scrollToIndex(0);
        }}
        className={clsx(
          activeSection.kind === 'summary'
            ? buttonStyles.active
            : buttonStyles.inactive,
        )}
        plain
      >
        <span className="text-xs">Providers</span>
      </Button>

      {providers.map((provider, index) => (
        <Button
          key={provider.id}
          onClick={() => {
            scrollToIndex(index + 1);
          }}
          className={clsx(
            isActive(index) ? buttonStyles.active : buttonStyles.inactive,
          )}
          plain
        >
          <span className="truncate text-xs">
            {provider.name || 'Unnamed Provider'}
          </span>
          {provider.provider_contact_ref_id && (
            <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 bg-green-100 text-green-700 rounded flex-shrink-0">
              ✓
            </span>
          )}
        </Button>
      ))}

      <Button
        onClick={onAddProvider}
        outline
        className="flex items-center gap-1 text-xs py-1! px-2!"
      >
        <Plus className="h-3 w-3" /> Add Provider
      </Button>
    </Card>
  );
};

export const TreatmentOverview = () => {
  const { matterId } = useMatter();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: providers, isLoading } = useGetMedicalProvidersQuery(matterId);
  const summaryRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const providerRefs = useRef<HTMLDivElement[]>([]);

  const [activeSection, setActiveSection] = useState<ActiveSection | null>(
    null,
  );
  const [showDirectoryModal, setShowDirectoryModal] = useState(false);

  const { data: incidents } = useGetIncidentsQuery(
    { matterId },
    { skip: !showDirectoryModal },
  );

  const incidentCenter = useMemo(() => {
    const list = incidents ?? [];
    const best =
      list.find(
        (i) =>
          i.normalized_location?.latitude != null &&
          i.normalized_location?.longitude != null,
      ) ??
      list.find(
        (i) => i.extracted_latitude != null && i.extracted_longitude != null,
      );
    if (!best) return null;
    const lat = best.normalized_location?.latitude ?? best.extracted_latitude;
    const lng = best.normalized_location?.longitude ?? best.extracted_longitude;
    if (lat == null || lng == null) return null;
    return { lat, lng };
  }, [incidents]);

  // Get new design state from outlet context
  const { useNewDesign } = useOutletContext<TreatmentOutletContext>();

  // Get request modal params from URL
  const requestIdFromUrl = searchParams.get('request_id');
  const providerIdFromUrl = searchParams.get('provider_id');
  const isRequestModalOpen = !!(requestIdFromUrl && providerIdFromUrl);

  const handleCloseRequestModal = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('request_id');
    newSearchParams.delete('provider_id');
    const searchString = newSearchParams.toString();
    navigate(
      {
        search: searchString,
      },
      { replace: true },
    );
  };

  // Not really sure why this works but here is the sauce:
  // https://stackoverflow.com/questions/54633690/how-can-i-use-multiple-refs-for-an-array-of-elements-with-hooks
  useEffect(() => {
    if (providers) {
      providerRefs.current = providerRefs.current.slice(0, providers.length);
    }
  }, [providers]);

  const approvedProviders = useMemo(() => {
    if (!providers) return [];
    return providers.filter((p) => p.status === ProviderStatus.APPROVED);
  }, [providers]);

  const handleProviderCreated = () => {
    setShowDirectoryModal(false);
    // Providers will auto-refresh via the query
  };

  return (
    <div className="bg-white min-h-full">
      {useNewDesign ? (
        // New Design
        <TreatmentOverviewNew />
      ) : (
        // Old Design
        <div className="flex flex-row gap-4 h-full pr-4 w-full">
          <TreatmentSidebar
            summaryRef={summaryRef}
            providerRefs={providerRefs}
            providers={approvedProviders ?? []}
            containerRef={containerRef}
            onActiveSectionChange={setActiveSection}
            onAddProvider={() => setShowDirectoryModal(true)}
          />
          <div
            className="flex flex-col gap-12 h-full overflow-auto no-scrollbar flex-1 min-w-0"
            ref={containerRef}
          >
            <TreatmentSummary
              ref={summaryRef}
              providers={providers ?? []}
              isLoading={isLoading}
              isActive={activeSection?.kind === 'summary'}
            />
            {approvedProviders?.map((provider, index) => (
              <ProviderOverview
                key={provider.id}
                provider={provider}
                ref={(el) => {
                  if (el) {
                    providerRefs.current[index] = el;
                  }
                }}
                isActive={
                  activeSection?.kind === 'provider' &&
                  activeSection.index === index
                }
                useNewDesign={false}
              />
            ))}
            <div className="min-h-64 w-full" />
          </div>
        </div>
      )}

      {isRequestModalOpen && requestIdFromUrl && providerIdFromUrl && (
        <RequestPageModal
          isOpen={isRequestModalOpen}
          setIsOpen={(open) => {
            if (!open) {
              handleCloseRequestModal();
            }
          }}
          requestId={requestIdFromUrl}
          providerId={providerIdFromUrl}
          highZIndex
        />
      )}

      <AddProviderModal
        isOpen={showDirectoryModal}
        onClose={() => setShowDirectoryModal(false)}
        matterId={matterId}
        centerLat={incidentCenter?.lat}
        centerLng={incidentCenter?.lng}
        onProviderCreated={handleProviderCreated}
      />
    </div>
  );
};
